<div class="w-full space-y-6">
  <!-- Header Section -->
  <div class="flex items-center justify-between">
    <div>
      <h1 class="text-2xl font-bold text-gray-800">Account Subscriptions</h1>
      <p class="text-gray-600 mt-1">Manage your active subscriptions and billing</p>
    </div>
    <p-button class="bg-sky-500 text-white px-4 py-2 rounded-md"
      label="Add Subscription" 
      icon="pi pi-plus">
    </p-button>
  </div>

  <!-- Stats Cards -->
  <div class="grid grid-cols-1 md:grid-cols-4 gap-6">
    <div class="bg-white p-6 rounded-xl shadow-lg border border-sky-100">
      <div class="flex items-center justify-between">
        <div>
          <p class="text-sm text-gray-600">Total Subscriptions</p>
          <p class="text-2xl font-bold text-gray-800">{{ subscriptions.length }}</p>
        </div>
        <div class="w-12 h-12 bg-sky-100 rounded-lg flex items-center justify-center">
          <i class="pi pi-list text-sky-600 text-xl"></i>
        </div>
      </div>
    </div>

    <div class="bg-white p-6 rounded-xl shadow-lg border border-sky-100">
      <div class="flex items-center justify-between">
        <div>
          <p class="text-sm text-gray-600">Active Subscriptions</p>
          <p class="text-2xl font-bold text-green-600">{{ getTotalActiveSubscriptions() }}</p>
        </div>
        <div class="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
          <i class="pi pi-check-circle text-green-600 text-xl"></i>
        </div>
      </div>
    </div>

    <div class="bg-white p-6 rounded-xl shadow-lg border border-sky-100">
      <div class="flex items-center justify-between">
        <div>
          <p class="text-sm text-gray-600">Monthly Revenue</p>
          <p class="text-2xl font-bold text-sky-600">{{ formatCurrency(getTotalMonthlyRevenue()) }}</p>
        </div>
        <div class="w-12 h-12 bg-sky-100 rounded-lg flex items-center justify-center">
          <i class="pi pi-calendar text-sky-600 text-xl"></i>
        </div>
      </div>
    </div>

    <div class="bg-white p-6 rounded-xl shadow-lg border border-sky-100">
      <div class="flex items-center justify-between">
        <div>
          <p class="text-sm text-gray-600">Yearly Revenue</p>
          <p class="text-2xl font-bold text-blue-600">{{ formatCurrency(getTotalYearlyRevenue()) }}</p>
        </div>
        <div class="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
          <i class="pi pi-dollar text-blue-600 text-xl"></i>
        </div>
      </div>
    </div>
  </div>

  <!-- Filters and Search -->
  <div class="bg-white p-6 rounded-xl shadow-lg border border-sky-100">
    <div class="grid grid-cols-1 md:grid-cols-4 gap-4">
      <!-- Search -->
      <div class="md:col-span-2">
        <label class="block text-sm font-medium text-gray-700 mb-2">Search Subscriptions</label>
        <div class="relative">
          <input
            type="text"
            [(ngModel)]="searchTerm"
            placeholder="Search by name or product..."
            class="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-sky-500"
          />
          <i class="pi pi-search absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
        </div>
      </div>

      <!-- Type Filter -->
      <div>
        <label class="block text-sm font-medium text-gray-700 mb-2">Filter by Type</label>
        <select
          [(ngModel)]="filterType"
          class="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-sky-500"
        >
          <option *ngFor="let option of typeOptions" [value]="option.value">{{ option.label }}</option>
        </select>
      </div>

      <!-- Status Filter -->
      <div>
        <label class="block text-sm font-medium text-gray-700 mb-2">Filter by Status</label>
        <select
          [(ngModel)]="filterStatus"
          class="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-sky-500"
        >
          <option *ngFor="let option of statusOptions" [value]="option.value">{{ option.label }}</option>
        </select>
      </div>
    </div>
  </div>

  <!-- Subscriptions List -->
  <div class="bg-white rounded-xl shadow-lg border border-sky-100 overflow-hidden">
    <div class="px-6 py-4 bg-gradient-to-r from-sky-50 to-blue-50 border-b border-sky-100">
      <h2 class="text-lg font-semibold text-gray-800">Subscriptions ({{ filteredSubscriptions.length }})</h2>
    </div>
    
    <div class="divide-y divide-gray-200">
      <div 
        *ngFor="let subscription of filteredSubscriptions" 
        class="p-6 hover:bg-sky-50 transition-colors cursor-pointer"
        (click)="selectSubscription(subscription)"
      >
        <div class="flex items-center justify-between">
          <div class="flex-1">
            <div class="flex items-center space-x-4">
              <!-- Product Icon -->
              <div class="w-12 h-12 bg-sky-100 rounded-lg flex items-center justify-center">
                <i class="pi pi-box text-sky-600 text-xl"></i>
              </div>
              
              <!-- Subscription Details -->
              <div class="flex-1">
                <div class="flex items-center space-x-3 mb-2">
                  <h3 class="text-lg font-semibold text-gray-800">{{ subscription.name }}</h3>
                  <span [class]="'px-2 py-1 rounded-full text-xs font-medium ' + getStatusBadgeClass(subscription.status || 'inactive')">
                    {{ subscription.status || 'inactive' }}
                  </span>
                  <span [class]="'px-2 py-1 rounded-full text-xs font-medium ' + getTypeBadgeClass(subscription.type)">
                    {{ subscription.type }}
                  </span>
                </div>
                
                <div class="flex items-center space-x-4 text-sm text-gray-600">
                  <span class="flex items-center">
                    <i class="pi pi-calendar mr-1"></i>
                    {{ subscription.billingCycle }}
                  </span>
                  <span class="flex items-center">
                    <i class="pi pi-dollar mr-1"></i>
                    {{ formatCurrency(subscription.baseFee) }}
                  </span>
                  <span *ngIf="subscription.nextBillingDate" class="flex items-center">
                    <i class="pi pi-clock mr-1"></i>
                    Next: {{ formatDate(subscription.nextBillingDate) }}
                  </span>
                </div>
              </div>
            </div>
          </div>
          
          <!-- Features Preview -->
          <div class="hidden md:block">
            <div class="text-sm text-gray-600">
              <span class="font-medium">{{ subscription.features.length }}</span> features
            </div>
            <div class="flex flex-wrap gap-1 mt-1">
              <span 
                *ngFor="let feature of subscription.features.slice(0, 2)" 
                class="px-2 py-1 bg-sky-100 text-sky-700 text-xs rounded-full"
              >
                {{ feature }}
              </span>
              <span 
                *ngIf="subscription.features.length > 2" 
                class="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full"
              >
                +{{ subscription.features.length - 2 }} more
              </span>
            </div>
          </div>
          
          <!-- Arrow Icon -->
          <div class="ml-4">
            <i class="pi pi-chevron-right text-gray-400"></i>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>

<!-- Right Side Drawer -->
<div 
  class="fixed inset-0 z-50 overflow-hidden"
  [class.hidden]="!drawerOpen"
>
  <!-- Backdrop -->
  <div 
    class="absolute inset-0 bg-black bg-opacity-50 transition-opacity"
    (click)="closeDrawer()"
  ></div>
  
  <!-- Drawer -->
  <div class="absolute right-0 top-0 h-full w-full max-w-md bg-white shadow-2xl transform transition-transform">
    <div class="flex flex-col h-full">
      <!-- Drawer Header -->
      <div class="px-6 py-4 bg-gradient-to-r from-sky-500 to-sky-600 text-white">
        <div class="flex items-center justify-between">
          <h2 class="text-xl font-semibold">Subscription Details</h2>
          <button 
            (click)="closeDrawer()"
            class="p-2 hover:bg-sky-600 rounded-lg transition-colors"
          >
            <i class="pi pi-times text-xl"></i>
          </button>
        </div>
      </div>
      
      <!-- Drawer Content -->
      <div class="flex-1 overflow-y-auto p-6" *ngIf="selectedSubscription">
        <!-- Subscription Info -->
        <div class="mb-6">
          <div class="flex items-center space-x-4 mb-4">
            <div class="w-16 h-16 bg-sky-100 rounded-xl flex items-center justify-center">
              <i class="pi pi-box text-sky-600 text-2xl"></i>
            </div>
            <div>
              <h3 class="text-2xl font-bold text-gray-800">{{ selectedSubscription.name }}</h3>
              <p class="text-gray-600">{{ selectedSubscription.product }}</p>
            </div>
          </div>
          
          <div class="flex space-x-2">
            <span [class]="'px-3 py-1 rounded-full text-sm font-medium ' + getStatusBadgeClass(selectedSubscription.status || 'inactive')">
              {{ selectedSubscription.status || 'inactive' }}
            </span>
            <span [class]="'px-3 py-1 rounded-full text-sm font-medium ' + getTypeBadgeClass(selectedSubscription.type)">
              {{ selectedSubscription.type }}
            </span>
            <span [class]="'px-3 py-1 rounded-full text-sm font-medium ' + getBillingCycleClass(selectedSubscription.billingCycle)">
              {{ selectedSubscription.billingCycle }}
            </span>
          </div>
        </div>

        <!-- Pricing -->
        <div class="bg-sky-50 p-4 rounded-lg mb-6">
          <h4 class="font-semibold text-gray-800 mb-2">Pricing</h4>
          <div class="text-3xl font-bold text-sky-600">{{ formatCurrency(selectedSubscription.baseFee) }}</div>
          <div class="text-sm text-gray-600">per {{ selectedSubscription.billingCycle }}</div>
        </div>

        <!-- Dates -->
        <div class="grid grid-cols-2 gap-4 mb-6">
          <div *ngIf="selectedSubscription.startDate">
            <h4 class="font-semibold text-gray-800 mb-1">Start Date</h4>
            <p class="text-gray-600">{{ formatDate(selectedSubscription.startDate) }}</p>
          </div>
          <div *ngIf="selectedSubscription.nextBillingDate">
            <h4 class="font-semibold text-gray-800 mb-1">Next Billing</h4>
            <p class="text-gray-600">{{ formatDate(selectedSubscription.nextBillingDate) }}</p>
          </div>
          <div *ngIf="selectedSubscription.endDate">
            <h4 class="font-semibold text-gray-800 mb-1">End Date</h4>
            <p class="text-gray-600">{{ formatDate(selectedSubscription.endDate) }}</p>
          </div>
        </div>

        <!-- Features -->
        <div class="mb-6">
          <h4 class="font-semibold text-gray-800 mb-4">Features ({{ selectedSubscription.features.length }})</h4>
          <div class="space-y-2">
            <div 
              *ngFor="let feature of selectedSubscription.features" 
              class="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg"
            >
              <i class="pi pi-check text-green-600"></i>
              <span class="text-gray-700">{{ feature }}</span>
            </div>
          </div>
        </div>

        <!-- Actions -->
        <div class="space-y-3">
          <button 
            class="w-full bg-sky-500 text-white py-3 px-4 rounded-lg font-semibold hover:bg-sky-600 transition-colors"
            *ngIf="selectedSubscription.isActive"
          >
            Manage Subscription
          </button>
          <button 
            class="w-full bg-gray-100 text-gray-700 py-3 px-4 rounded-lg font-semibold hover:bg-gray-200 transition-colors"
            *ngIf="!selectedSubscription.isActive"
          >
            Reactivate Subscription
          </button>
          <button 
            class="w-full bg-red-100 text-red-700 py-3 px-4 rounded-lg font-semibold hover:bg-red-200 transition-colors"
            *ngIf="selectedSubscription.isActive"
          >
            Cancel Subscription
          </button>
        </div>
      </div>
    </div>
  </div>
</div>